import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CampaignAnalytics as Analytics } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { formatDuration, formatMoney, formatNumber } from '../../lib/format.js';
import { Empty, ErrorBanner, Segmented, Stat, StatGroup, TableSkeleton } from '../../components/ui/Primitives.js';

/**
 * How a campaign is doing (docs/36 §8.7): what became of its leads, how its calls went, who made
 * them, when calls get answered, and what it cost. Counted from the calls themselves.
 */

type Range = 'all' | 'today' | '7' | '30';
const RANGES: ReadonlyArray<readonly [Range, string]> = [['all', 'All time'], ['today', 'Today'], ['7', '7 days'], ['30', '30 days']];

const OUTCOME_LABEL: Record<string, string> = {
  answered: 'Answered', no_answer: 'No answer', busy: 'Busy', failed: 'Could not connect', machine: 'Answering machine',
  abandoned: 'Hung up before the message', optout: 'Opted out', transferred: 'Pressed to speak to someone',
  message: 'Heard the message', no_key: 'Heard it, pressed nothing', unknown: 'Unknown',
};

const OUTCOME_COLOUR: Record<string, string> = {
  answered: 'var(--arc-ok)', transferred: 'var(--arc-ok)', message: 'var(--arc-c1)', no_key: 'var(--arc-c4)',
  no_answer: 'var(--arc-fg-4)', busy: 'var(--arc-warn)', failed: 'var(--arc-danger)', machine: 'var(--arc-info)',
  abandoned: 'var(--arc-c5)', optout: 'var(--arc-danger)',
};
const EFFECT_COLOUR: Record<string, string> = { final: 'var(--arc-ok)', retry: 'var(--arc-warn)', next_number: 'var(--arc-info)' };
const AMD_COLOUR: Record<string, string> = { HUMAN: 'var(--arc-ok)', MACHINE: 'var(--arc-info)', NOTSURE: 'var(--arc-warn)', SCREENER: 'var(--arc-c4)' };
const AMD_LABEL: Record<string, string> = { HUMAN: 'A person', MACHINE: 'A machine', NOTSURE: 'Not sure', SCREENER: 'A call screen' };

/** The decisions by result, whichever engine made them. */
const amdByResult = (rows: Analytics['amd']) => {
  const m = new Map<string, number>();
  for (const r of rows) m.set(r.result, (m.get(r.result) ?? 0) + r.n);
  return [...m].sort((a, b) => b[1] - a[1]).map(([result, n]) => ({ key: result, label: AMD_LABEL[result] ?? result, n, colour: AMD_COLOUR[result] }));
};
const PALETTE = ['var(--arc-ok)', 'var(--arc-c1)', 'var(--arc-c4)', 'var(--arc-c5)', 'var(--arc-c3)', 'var(--arc-fg-4)'];

/** A rate as a status colour: green when good, amber when middling, red when poor. */
const rateTone = (n: number, of: number, good: number, fair: number): 'ok' | 'warn' | 'danger' | undefined =>
  !of ? undefined : n / of >= good ? 'ok' : n / of >= fair ? 'warn' : 'danger';

const day = (d: Date): string => d.toLocaleDateString('en-CA');
const pct = (n: number, of: number): string => (of ? `${Math.round((n / of) * 100)}%` : '—');

function rangeParams(r: Range): string {
  if (r === 'all') return '';
  const to = new Date();
  const from = new Date(to);
  if (r !== 'today') from.setDate(from.getDate() - (Number(r) - 1));
  return `?from=${day(from)}&to=${day(to)}`;
}

export function CampaignAnalytics({ tenantId, id }: { tenantId: string; id: string }) {
  const [range, setRange] = useState<Range>('all');
  const q = useQuery({
    queryKey: ['dialler', tenantId, 'campaign-analytics', id, range],
    queryFn: () => api.get<Analytics>(`/v1/tenants/${tenantId}/campaigns/${id}/analytics${rangeParams(range)}`),
    placeholderData: (prev) => prev,
    refetchInterval: 60_000,
  });
  const a = q.data;

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 12 }}>
        <Segmented label="Range" value={range} onChange={setRange} options={RANGES} />
      </div>
      {q.error && <ErrorBanner error={q.error} />}
      {!a ? <TableSkeleton rows={4} cols={4} /> : <Body a={a} />}
    </>
  );
}

function Body({ a }: { a: Analytics }) {
  const c = a.calls;
  const finalCount = a.dispositions.filter((d) => d.effect === 'final').reduce((n, d) => n + d.n, 0);
  return (
    <>
      <StatGroup>
        <Stat bare icon="check" hue={1} label="Leads reached" value={pct(a.leads.reached, a.leads.total)}
          sub={`${formatNumber(a.leads.reached)} of ${formatNumber(a.leads.total)} · ${formatNumber(a.leads.remaining)} still to call`} />
        <Stat bare icon="callOut" hue={2} label="Calls" value={formatNumber(c.total)} sub={`${pct(c.answered, c.total)} answered`} />
        <Stat bare icon="users" hue={3} label="Contact rate" value={pct(c.connected, c.total)} tone={rateTone(c.connected, c.total, 0.35, 0.2)} sub="calls that reached a person" />
        <Stat bare icon="clock" hue={4} label="Talk time" value={formatDuration(c.talk_sec)} sub={c.connected ? `${formatDuration(Math.round(c.talk_sec / c.connected))} a contact` : undefined} />
        {a.cost && (
          <Stat bare icon="billing" hue={5} label="Cost" value={formatMoney(a.cost.amount.toFixed(2), a.cost.currency)}
            sub={a.leads.reached ? `${formatMoney((a.cost.amount / a.leads.reached).toFixed(2), a.cost.currency)} a lead reached` : undefined} />
        )}
      </StatGroup>

      {c.total === 0 ? (
        <div className="arc-card" style={{ marginTop: 14 }}><Empty icon="phone" title="No calls in this range" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginTop: 14 }}>
          <Shares title="How the calls went" rows={c.by_outcome.map((o) => ({ key: o.outcome, label: OUTCOME_LABEL[o.outcome] ?? o.outcome, n: o.n, colour: OUTCOME_COLOUR[o.outcome] }))} of={c.total} />
          <Shares title="Dispositions" empty="No dispositions yet."
            rows={a.dispositions.map((d) => ({ key: d.code, label: d.label, n: d.n, colour: EFFECT_COLOUR[d.effect ?? ''], note: d.effect === 'final' ? 'final' : d.effect === 'next_number' ? 'next number' : 'try again' }))}
            of={a.dispositions.reduce((n, d) => n + d.n, 0)}
            foot={finalCount ? `${formatNumber(finalCount)} final: ${pct(finalCount, c.total)} of calls` : undefined} />
          <Hours rows={a.by_hour} />
          {a.calls_to_reach.length > 0 && (
            <Shares title="Calls it took to reach a lead" rows={a.calls_to_reach.map((r) => ({ key: String(r.calls), label: r.calls >= 10 ? '10 or more' : `${r.calls} call${r.calls === 1 ? '' : 's'}`, n: r.n, colour: PALETTE[Math.min(r.calls - 1, PALETTE.length - 1)] }))}
              of={a.calls_to_reach.reduce((n, r) => n + r.n, 0)} />
          )}
          {a.amd.length > 0 && (
            <div>
              <Shares title="Answering machine detection" rows={amdByResult(a.amd)} of={a.amd.reduce((n, r) => n + r.n, 0)} />
              {/* Which engine decided, and what the AI checks cost (0135). */}
              <div className="arc-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                {(() => {
                  const own = a.amd.filter((r) => r.engine !== 'ai').reduce((n, r) => n + r.n, 0);
                  const ai = a.amd.filter((r) => r.engine === 'ai');
                  const aiN = ai.reduce((n, r) => n + r.n, 0);
                  const cost = ai.reduce((n, r) => n + Number(r.charged ?? 0), 0);
                  return <>Our detector decided {formatNumber(own)}; the AI check {formatNumber(aiN)}
                    {aiN > 0 ? (cost > 0 ? `, costing ${formatMoney(cost.toFixed(2), a.cost?.currency ?? 'ZAR')}` : ', on your own key') : ''}.</>;
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {a.agents.length > 0 && (
        <div className="arc-card" style={{ marginTop: 14 }}>
          <div className="arc-card-body" style={{ paddingBottom: 0 }}><h3 className="arc-h2" style={{ margin: 0 }}>Agents</h3></div>
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Agent</th><th className="arc-td-right">Calls</th><th className="arc-td-right">Contact rate</th>
                <th className="arc-td-right">Final</th><th className="arc-td-right">Talk time</th><th className="arc-td-right">Average wrap-up</th></tr></thead>
              <tbody>
                {a.agents.map((g) => (
                  <tr key={g.extension_id}>
                    <td>{g.name}</td>
                    <td className="arc-td-right arc-num">{formatNumber(g.calls)}</td>
                    <td className="arc-td-right arc-num">
                      <div className="arc-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                        <span className="arc-meter" aria-hidden="true" style={{ width: 70 }}>
                          <span style={{ width: `${g.calls ? (g.connected / g.calls) * 100 : 0}%`, background: `var(--arc-${rateTone(g.connected, g.calls, 0.35, 0.2) ?? 'ok'})` }} />
                        </span>
                        {pct(g.connected, g.calls)}
                      </div>
                    </td>
                    <td className="arc-td-right arc-num" style={{ color: g.final ? 'var(--arc-ok)' : undefined, fontWeight: g.final ? 600 : undefined }}>{formatNumber(g.final)}</td>
                    <td className="arc-td-right arc-num">{formatDuration(g.talk_sec)}</td>
                    <td className="arc-td-right arc-num">{g.wrap_sec === null ? '—' : formatDuration(g.wrap_sec)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {a.by_day.length > 1 && (
        <div className="arc-card" style={{ marginTop: 14 }}>
          <div className="arc-card-body" style={{ paddingBottom: 0 }}><h3 className="arc-h2" style={{ margin: 0 }}>By day</h3></div>
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Day</th><th className="arc-td-right">Calls</th><th className="arc-td-right">Reached a person</th><th className="arc-td-right">Reached, final</th></tr></thead>
              <tbody>
                {[...a.by_day].reverse().map((d) => (
                  <tr key={d.day}>
                    <td className="arc-num">{d.day}</td>
                    <td className="arc-td-right arc-num">{formatNumber(d.calls)}</td>
                    <td className="arc-td-right arc-num">{formatNumber(d.connected)} <span className="arc-subtle">({pct(d.connected, d.calls)})</span></td>
                    <td className="arc-td-right arc-num" style={{ color: d.reached ? 'var(--arc-ok)' : undefined, fontWeight: d.reached ? 600 : undefined }}>{formatNumber(d.reached)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

/** A breakdown: each row's count, its share, and the share again as a bar for scanning. */
function Shares({ title, rows, of, empty, foot }: {
  title: string; rows: Array<{ key: string; label: string; n: number; note?: string; colour?: string | undefined }>; of: number; empty?: string; foot?: string | undefined;
}) {
  return (
    <div className="arc-card arc-card-body">
      <h3 className="arc-h2" style={{ margin: '0 0 8px' }}>{title}</h3>
      {rows.length === 0 ? <p className="arc-subtle" style={{ margin: 0 }}>{empty ?? 'Nothing yet.'}</p> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <div key={r.key}>
              <div className="arc-row" style={{ gap: 8, fontSize: 13 }}>
                <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, flex: 'none', background: r.colour ?? 'var(--arc-ok)' }} />
                <span>{r.label}{r.note && <span className="arc-subtle" style={{ fontSize: 11.5 }}> · {r.note}</span>}</span>
                <div className="arc-spacer" />
                <span className="arc-num">{formatNumber(r.n)}</span>
                <span className="arc-num arc-subtle" style={{ width: 40, textAlign: 'right' }}>{pct(r.n, of)}</span>
              </div>
              <span className="arc-meter" aria-hidden="true" style={{ marginTop: 3 }}><span style={{ width: `${of ? (r.n / of) * 100 : 0}%`, background: r.colour ?? 'var(--arc-ok)' }} /></span>
            </div>
          ))}
        </div>
      )}
      {foot && <p className="arc-hint" style={{ margin: '10px 0 0' }}>{foot}</p>}
    </div>
  );
}

/** Calls by the hour they were made, and how many reached a person: when to call. */
function Hours({ rows }: { rows: Analytics['by_hour'] }) {
  const hours = rows.filter((r) => r.calls > 0);
  const lo = Math.min(...hours.map((h) => h.hour), 8);
  const hi = Math.max(...hours.map((h) => h.hour), 19);
  const max = Math.max(1, ...hours.map((h) => h.calls));
  const at = new Map(rows.map((r) => [r.hour, r]));
  const span = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  return (
    <div className="arc-card arc-card-body">
      <h3 className="arc-h2" style={{ margin: '0 0 8px' }}>By hour</h3>
      <div className="arc-row" style={{ gap: 3, alignItems: 'flex-end', height: 110 }} role="img"
        aria-label={span.map((h) => `${h}:00 ${at.get(h)?.calls ?? 0} calls, ${at.get(h)?.connected ?? 0} reached`).join('; ')}>
        {span.map((h) => {
          const r = at.get(h);
          const calls = r?.calls ?? 0;
          const conn = r?.connected ?? 0;
          return (
            <div key={h} title={`${String(h).padStart(2, '0')}:00 — ${calls} calls, ${conn} reached a person (${pct(conn, calls)})`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
              <div style={{ height: `${(calls / max) * 100}%`, background: 'var(--arc-info-bg)', border: '1px solid var(--arc-info-border)', borderBottom: 'none', borderRadius: '3px 3px 0 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: calls ? 2 : 0 }}>
                <div style={{ height: `${calls ? (conn / calls) * 100 : 0}%`, background: 'var(--arc-ok)', borderRadius: conn === calls ? '3px 3px 0 0' : 0 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="arc-row arc-subtle arc-num" style={{ gap: 3, fontSize: 10.5, marginTop: 4 }}>
        {span.map((h) => <span key={h} style={{ flex: 1, textAlign: 'center' }}>{h % 2 === 0 ? String(h).padStart(2, '0') : ''}</span>)}
      </div>
      <div className="arc-row arc-hint" style={{ gap: 12, margin: '8px 0 0' }}>
        <span className="arc-row" style={{ gap: 5 }}><span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--arc-info-bg)', border: '1px solid var(--arc-info-border)' }} />Calls made</span>
        <span className="arc-row" style={{ gap: 5 }}><span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--arc-ok)' }} />Reached a person</span>
      </div>
    </div>
  );
}
